import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Autoplay from 'embla-carousel-autoplay';
import { Building2, Landmark, ShieldCheck, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from './ui/carousel';
import { marketingAPI } from '../utils/api';
import { trackAdImpressions, trackAdClick } from '../utils/marketingTracking';

const ICONS_BY_TYPE = {
  bank: Landmark,
  developer: Building2,
  insurance: ShieldCheck,
  other: Sparkles,
};
const ACCENT_BY_TYPE = {
  bank: { accent: 'bg-blue-700', badge: 'bg-blue-100 text-blue-800', gradient: 'from-blue-50 to-sky-50', label: 'Banque Partenaire' },
  developer: { accent: 'bg-orange-600', badge: 'bg-orange-100 text-orange-700', gradient: 'from-orange-50 to-amber-50', label: 'Développement Foncier' },
  insurance: { accent: 'bg-indigo-700', badge: 'bg-indigo-100 text-indigo-800', gradient: 'from-indigo-50 to-violet-50', label: 'Assurance' },
  other: { accent: 'bg-emerald-700', badge: 'bg-emerald-100 text-emerald-800', gradient: 'from-emerald-50 to-teal-50', label: 'Annonceur' },
};

const homeAds = [
  {
    id: 'dev-1',
    category: 'Développement Foncier',
    icon: Building2,
    title: 'Résidence Les Cocotiers',
    subtitle: 'MAETUR Cameroun • Douala PK14',
    description:
      "120 villas et appartements haut standing avec piscine, sécurité 24/7. Livraison Décembre 2026.",
    cta: 'Découvrir le projet',
    ctaLink: '/developments',
    image:
      'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800',
    accent: 'bg-orange-600',
    badge: 'bg-orange-100 text-orange-700',
    gradient: 'from-orange-50 to-amber-50',
    testid: 'home-ad-development',
  },
  {
    id: 'bank-1',
    category: 'Banque Partenaire',
    icon: Landmark,
    title: 'Afriland First Bank',
    subtitle: 'Prêt Immobilier • Taux dès 6.5%',
    description:
      "Financement jusqu'à 25 ans. Réponse en 48h. Réalisez votre rêve immobilier dès aujourd'hui.",
    cta: 'Simuler mon prêt',
    ctaLink: '/loan-calculator',
    image:
      'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800',
    accent: 'bg-blue-700',
    badge: 'bg-blue-100 text-blue-800',
    gradient: 'from-blue-50 to-sky-50',
    testid: 'home-ad-bank',
  },
  {
    id: 'insurance-1',
    category: 'Assurance Habitation',
    icon: ShieldCheck,
    title: 'AXA Cameroun',
    subtitle: 'Multirisque Habitation • 24/7',
    description:
      "Protégez votre maison contre l'incendie, le vol et les dégâts des eaux. Indemnisation rapide.",
    cta: 'Obtenir un devis',
    ctaLink: '/contact',
    image:
      'https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=800',
    accent: 'bg-indigo-700',
    badge: 'bg-indigo-100 text-indigo-800',
    gradient: 'from-indigo-50 to-violet-50',
    testid: 'home-ad-insurance',
  },
  {
    id: 'dev-2',
    category: 'Développement Foncier',
    icon: Building2,
    title: 'Cité du Progrès',
    subtitle: 'SIC Immobilier • Yaoundé Nkolbisson',
    description:
      "200 parcelles viabilisées. Routes goudronnées, eau, électricité, fibre optique. Titre foncier.",
    cta: 'Voir les parcelles',
    ctaLink: '/developments',
    image:
      'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=800',
    accent: 'bg-orange-600',
    badge: 'bg-orange-100 text-orange-700',
    gradient: 'from-orange-50 to-amber-50',
    testid: 'home-ad-development-2',
  },
  {
    id: 'bank-2',
    category: 'Banque Partenaire',
    icon: Landmark,
    title: 'Ecobank Cameroun',
    subtitle: 'Crédit Immobilier • Sans apport',
    description:
      "Financement jusqu'à 80% du bien. Pas d'apport initial requis. Simulation gratuite en ligne.",
    cta: 'En savoir plus',
    ctaLink: '/loan-calculator',
    image:
      'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800',
    accent: 'bg-emerald-700',
    badge: 'bg-emerald-100 text-emerald-800',
    gradient: 'from-emerald-50 to-teal-50',
    testid: 'home-ad-bank-2',
  },
  {
    id: 'insurance-2',
    category: 'Assurance Emprunteur',
    icon: ShieldCheck,
    title: 'Allianz Cameroun',
    subtitle: 'Protection Famille • Sérénité',
    description:
      "Couverture décès, invalidité et perte d'emploi. Sérénité garantie pour votre famille.",
    cta: 'Découvrir la formule',
    ctaLink: '/contact',
    image:
      'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800',
    accent: 'bg-blue-800',
    badge: 'bg-blue-100 text-blue-900',
    gradient: 'from-blue-50 to-indigo-50',
    testid: 'home-ad-insurance-2',
  },
];

const HomeAdCarousel = () => {
  // Stable Autoplay instance — using useMemo (not useRef) to avoid
  // reading `.current` during render which the React Compiler flags as
  // unsafe via the `react-hooks/refs` rule.
  const plugin = React.useMemo(
    () => Autoplay({ delay: 4000, stopOnInteraction: false }),
    []
  );

  const opts = React.useMemo(
    () => ({ align: 'start', loop: true }),
    []
  );

  const [ads, setAds] = useState(homeAds);

  useEffect(() => {
    marketingAPI
      .getActiveAds()
      .then((r) => {
        const active = r.data.ads || [];
        if (active.length > 0) {
          // Map backend ads to carousel slide format
          const mapped = active.map((a) => {
            const style = ACCENT_BY_TYPE[a.advertiser_type] || ACCENT_BY_TYPE.other;
            const Icon = ICONS_BY_TYPE[a.advertiser_type] || ICONS_BY_TYPE.other;
            return {
              id: a.id,
              liveId: a.id,
              category: a.category || style.label,
              icon: Icon,
              title: a.title || a.company_name,
              subtitle: a.company_name + (a.slogan ? ` • ${a.slogan}` : ''),
              description: a.description,
              cta: 'En savoir plus',
              ctaLink: a.link || '#',
              image: a.image_url,
              accent: style.accent,
              badge: style.badge,
              gradient: style.gradient,
              testid: `home-ad-live-${a.id}`,
            };
          });
          // Show live ads first, then fallback static ads
          setAds([...mapped, ...homeAds]);
          // Track impressions (deduplicated per session)
          trackAdImpressions(active.map((a) => a.id));
        }
      })
      .catch(() => {
        // keep static fallback
      });
  }, []);

  return (
    <div className="mb-16" data-testid="home-ad-carousel">
      <Carousel plugins={[plugin]} opts={opts} className="w-full">
        <CarouselContent>
          {ads.map((ad) => {
            const rawLink = (ad.ctaLink || '').trim();
            // A link is "valid" if it's a real internal path or a fully-qualified URL.
            // We reject empty values, '#', and obvious placeholders like 'example.cm'.
            const isInternal = rawLink.startsWith('/');
            const isExternal =
              /^https?:\/\//i.test(rawLink) && !/example\.(com|cm|org)/i.test(rawLink);
            const isValidLink = isInternal || isExternal;
            const handleClick = () => {
              if (ad.liveId) trackAdClick(ad.liveId);
            };
            const ctaButton = (
              <Button
                className={`${ad.accent} hover:opacity-90 text-white shadow-md`}
                data-testid={`${ad.testid}-cta`}
              >
                {ad.cta}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            );
            let ctaNode = null;
            if (isInternal) {
              ctaNode = (
                <Link
                  to={rawLink}
                  onClick={handleClick}
                  data-testid={`${ad.testid}-link`}
                >
                  {ctaButton}
                </Link>
              );
            } else if (isExternal) {
              ctaNode = (
                <a
                  href={rawLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleClick}
                  data-testid={`${ad.testid}-link`}
                >
                  {ctaButton}
                </a>
              );
            }
            // When !isValidLink, ctaNode stays null and the CTA is hidden — no
            // more `href="#"` clicks that go nowhere.
            return (
              <CarouselItem key={ad.id} className="basis-full">
                <div
                  className={`bg-gradient-to-r ${ad.gradient} rounded-2xl p-8 border border-gray-200 shadow-sm`}
                  data-testid={ad.testid}
                >
                  <div className="grid md:grid-cols-2 gap-8 items-center">
                    <div>
                      <div className="flex items-center gap-3 mb-4">
                        <div className={`inline-flex p-3 ${ad.accent} rounded-full shadow-md`}>
                          <ad.icon className="h-7 w-7 text-white" />
                        </div>
                        <span
                          className={`text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full ${ad.badge}`}
                        >
                          {ad.category}
                        </span>
                      </div>
                      <h2 className="text-3xl font-bold mb-1 text-gray-900">{ad.title}</h2>
                      <p className="text-sm text-gray-600 font-medium mb-4">{ad.subtitle}</p>
                      <p className="text-gray-700 mb-6 leading-relaxed">{ad.description}</p>
                      {ctaNode}
                    </div>
                    <div className="hidden md:block">
                      <img
                        src={ad.image}
                        alt={ad.title}
                        className="rounded-lg shadow-lg w-full h-64 object-cover"
                      />
                    </div>
                  </div>
                </div>
              </CarouselItem>
            );
          })}
        </CarouselContent>
      </Carousel>
    </div>
  );
};

export default HomeAdCarousel;
